import * as React from "react";
import styled, {
  css,
  useTheme
} from "styled-components";
import { lighten } from "polished";
import {
  useHistory,
  useLocation
} from "react-router-dom";

import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { Text } from "src/library/components/Text/Text";
import useOrder from "src/core/orders/hooks/useOrder";
import { calculateCartTotalQuantity } from "src/core/orders/utils/orders";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import { TotalPrice } from "src/core/prices/components/TotalPrice";
import { MenuContextType } from "src/core/shared/hooks/menu/useMenu";

interface SidebarOrderQuickActionProps {
  context: MenuContextType;
}

export const SidebarOrderQuickAction: React.ReactFCWithChildren<SidebarOrderQuickActionProps> = () => {
  const [ { currentOrder: { data: order } } ] = useOrder();
  const pricesVisible = usePriceVisibility();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();

  if (!order) {
    return null;
  }

  const productCount = order.orderLines ? calculateCartTotalQuantity(order.orderLines) : 0;
  const isOnOrderPage = location.pathname.startsWith(`/orders/${order.id}`);
  const destination = isOnOrderPage ? "/products" : `/orders/${order.id}`;
  const actionLabel = isOnOrderPage ? "Back to products" : "Checkout";

  return (
    <QuickActionWrapper
      onClick={() => history.push(destination)}
      data-cypress="sidebar-cart-quick-action"
    >
      <Icon
        icon={isOnOrderPage ? Icons.CHEVRONLEFT : Icons.CART}
        $fillColor="white"
        size={20}
      />

      <div className="cart-summary">
        <Text
          fontSize="sub"
          color={theme.colors.accentLight}
          className="action-label"
        >
          {actionLabel}
        </Text>

        <Text
          fontSize="sub"
          color={theme.overrides?.menu?.textColor ?? "white"}
          className="product-count"
        >
          {order.orderLines && pricesVisible ? (
            <>
              <TotalPrice
                lines={order.orderLines}
                id={order.id}
                totalOnly
                currency={order.branch?.priceCode?.currency}
              />

              {` · ${productCount} item${productCount !== 1 ? "s" : ""}`}
            </>
          ) : (
            `${productCount} item${productCount !== 1 ? "s" : ""}`
          )}
        </Text>
      </div>

      {!isOnOrderPage && (
        <Icon
          className="chevron"
          icon={Icons.CHEVRONRIGHT}
          $fillColor="white"
          size={20}
        />
      )}
    </QuickActionWrapper>
  );
};

const QuickActionWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-products: center;
    gap: ${theme.spacing.margins.sm};
    padding: ${theme.spacing.padding.md};
    background: ${theme.colors.primaryDark};
    cursor: pointer;
    min-height: 60px;
    border-top: 1px solid ${lighten(0.05, theme.colors.primaryDark)};

    ${theme.overrides?.menu?.iconColor ? css`
      svg {
        fill: ${theme.overrides.menu.iconColor} !important;
      }
    ` : ""}

    .cart-summary {
      flex: 1;
      display: flex;
      flex-direction: column;

      .action-label {
        color: ${theme.colors.accentLight};
        font-weight: ${theme.fonts.weights.bold};
        letter-spacing: 1px;
        text-transform: uppercase;
      }

      .product-count span {
        color: ${theme.overrides?.menu?.textColor ?? theme.colors.white};
      }
    }

    .chevron {
      transition: ${theme.transition?.quickest};
      transform: translateX(-4px);
    }

    &:hover {
      background: ${lighten(0.04, theme.colors.primaryDark)};

      .chevron {
        transform: translateX(0);
      }
    }
  `}
`;
